import type { LngLat, KeepOutZone } from './types';

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

// WGS84 — the datum every GPS receiver reports in.
const A = 6378137;
const F = 1 / 298.257223563;
const E2 = 2 * F - F * F;

/**
 * Local radii of curvature at a latitude: meridional (north-south) and normal
 * (east-west).
 *
 * A mean-radius sphere is off by 0.2-0.35% at mid latitudes, which is ~1.5 yards
 * over a 400m hole. That is a systematic bias, not noise, so it never averages
 * out — and being more accurate than a golfer pacing it off is the entire point
 * of the product. These radii make the local plane below exact to well under a
 * centimetre at hole scale.
 */
function curvature(latDeg: number): { m: number; n: number } {
  const s = Math.sin(toRad(latDeg));
  const w = 1 - E2 * s * s;
  return { m: (A * (1 - E2)) / Math.pow(w, 1.5), n: A / Math.sqrt(w) };
}

/**
 * Distance on the local tangent plane. Everything this app measures — tee to
 * green, buggy to fence, buggy to hole — is under a few km, where this is both
 * more accurate than haversine and cheaper to compute.
 */
export function distanceM(a: LngLat, b: LngLat): number {
  const midLat = (a[1] + b[1]) / 2;
  const { m, n } = curvature(midLat);
  const dNorth = m * toRad(b[1] - a[1]);
  const dEast = n * Math.cos(toRad(midLat)) * toRad(b[0] - a[0]);
  return Math.hypot(dNorth, dEast);
}

export function bearingDeg(a: LngLat, b: LngLat): number {
  const midLat = (a[1] + b[1]) / 2;
  const { m, n } = curvature(midLat);
  const dNorth = m * toRad(b[1] - a[1]);
  const dEast = n * Math.cos(toRad(midLat)) * toRad(b[0] - a[0]);
  return (toDeg(Math.atan2(dEast, dNorth)) + 360) % 360;
}

/**
 * Project a coordinate onto a local east/north plane in metres, relative to an
 * origin. Same curvature model as distanceM, so a hole drawn from these numbers
 * measures identically to the yardages quoted beside it — a schematic that
 * disagreed with its own distances would be worse than no schematic.
 *
 * Good to well under a centimetre across a golf hole; do not use it across a
 * continent, where the tangent plane stops being a plane.
 */
export function toLocalMetres(origin: LngLat, p: LngLat): { east: number; north: number } {
  const midLat = (origin[1] + p[1]) / 2;
  const { m, n } = curvature(midLat);
  return {
    east: n * Math.cos(toRad(midLat)) * toRad(p[0] - origin[0]),
    north: m * toRad(p[1] - origin[1]),
  };
}

export const METRES_PER_YARD = 0.9144;
export const toYards = (m: number) => m / METRES_PER_YARD;

export function formatDistance(metres: number, unit: 'm' | 'yd'): number {
  return Math.round(unit === 'yd' ? toYards(metres) : metres);
}

/**
 * A whole-trip distance as a label. Golf yardages stay in metres/yards, but a
 * road leg is kilometres or miles — "33.4 km" reads as a journey where "33445m"
 * reads as a bug.
 */
export function formatTotalDistance(metres: number, unit: 'm' | 'yd'): string {
  if (unit === 'yd') {
    const yd = toYards(metres);
    return yd >= 1760 ? `${(yd / 1760).toFixed(1)} mi` : `${Math.round(yd)} yd`;
  }
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${Math.round(metres)} m`;
}

/**
 * Playing distance = flat distance, adjusted for elevation and headwind.
 *
 * Elevation: the ball spends roughly the same flight time, so an uphill target
 * adds about 1m of required carry per 1m of rise (slightly less downhill,
 * because the ball keeps descending — hence the 0.8 factor on drops).
 *
 * Wind: only the component along the shot line matters. A rough but widely used
 * caddie rule is ~1% of carry per 1 m/s of head/tailwind, tail helping less than
 * head hurts. This is an aid, not a launch monitor — it is clamped hard.
 */
export function playingDistance(opts: {
  flatM: number;
  riseM?: number;
  windSpeed?: number;
  windDirFrom?: number;
  shotBearing?: number;
}): { metres: number; elevationAdj: number; windAdj: number } {
  const { flatM, riseM = 0, windSpeed = 0, windDirFrom, shotBearing } = opts;

  const elevationAdj = riseM >= 0 ? riseM : riseM * 0.8;

  let windAdj = 0;
  if (windSpeed > 0 && windDirFrom != null && shotBearing != null) {
    // windDirFrom is the meteorological "coming from" bearing.
    const windToward = (windDirFrom + 180) % 360;
    const delta = toRad(windToward - shotBearing);
    const along = Math.cos(delta) * windSpeed; // +ve = tailwind
    const pct = along >= 0 ? -along * 0.008 : -along * 0.012;
    windAdj = flatM * pct;
  }

  const total = flatM + elevationAdj + windAdj;
  // Never let the aid swing more than 15% — bad elevation data shouldn't
  // hand someone a 3-club error.
  const clamped = Math.min(Math.max(total, flatM * 0.85), flatM * 1.15);
  return { metres: clamped, elevationAdj, windAdj };
}

export function insideZone(p: LngLat, z: KeepOutZone): boolean {
  return distanceM(p, z.centre) <= z.radius;
}

export function firstBreachedZone(p: LngLat, zones: KeepOutZone[]): KeepOutZone | null {
  for (const z of zones) if (insideZone(p, z)) return z;
  return null;
}

/** Ray casting. Only used for hole corridors, which are small polygons. */
export function insidePolygon(p: LngLat, poly: LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect =
      yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Move `metres` along `bearing` from a point. Exact inverse of distanceM /
 * bearingDeg on the same local plane, so round trips do not drift.
 */
export function destination(from: LngLat, bearing: number, metres: number): LngLat {
  const br = toRad(bearing);
  const dNorth = Math.cos(br) * metres;
  const dEast = Math.sin(br) * metres;

  // Curvature is evaluated at the midpoint, so solve for the destination once
  // with the start latitude then correct with the true midpoint.
  let lat = from[1];
  for (let i = 0; i < 2; i++) {
    const { m } = curvature((from[1] + lat) / 2);
    lat = from[1] + toDeg(dNorth / m);
  }
  const midLat = (from[1] + lat) / 2;
  const { n } = curvature(midLat);
  const lng = from[0] + toDeg(dEast / (n * Math.cos(toRad(midLat))));
  return [lng, lat];
}
